import { ERROR_TYPE, IPagination } from "app/types";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { TICKET_API_URL } from "utils/constants";
import { handleApiError, parsedErrorMessage } from "utils/errorHandler";
import { request } from "utils/request";
import toastService from "utils/toast";
import { ISprintFilters, ISprints } from "../types";
import {
  selectButtonLoading,
  selectCustomizeTableHeader,
  selectPagination,
  selectSprintFilter,
  selectSprintFormDta,
} from "./selector";
import { actions } from "./slice";

export function* fetchCustomizeCardsRequest(action: { payload: string }) {
  try {
    yield delay(500);
    const id = action.payload;
    yield put(actions.setMainLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/customize-card/list/${id}`;
    const options = {
      method: "GET",
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setMainLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    const sprintCustomizeCards = data.data;
    yield put(actions.setCustomizeTableHeader(sprintCustomizeCards));
  } catch (err: any) {
    yield put(actions.setMainLoading(false));
    handleApiError(err);
  }
}

export function* fetchSprintListRequest(action: { payload: string }) {
  try {
    // Only add delay for first page to improve UX for infinite scroll
    const pagination: IPagination = yield select(selectPagination);
    if (pagination.page === 1) {
      yield delay(500);
    }

    const filters: ISprintFilters = yield select(selectSprintFilter);
    const { search, startDate, endDate, statusFilter } = filters;
    const buttonLoading: boolean = yield select(selectButtonLoading);
    if (!buttonLoading) {
      yield put(actions.setLoading(true));
    }
    const projectId = action.payload;
    const requestParams = `page=${pagination.page}&perPage=${pagination.perPage}`;
    const optionalRequestParams = `${
      pagination.orderBy.length > 0 ? "&orderBy=" + pagination.orderBy : ""
    }${
      pagination.orderBy.length > 0
        ? "&orderDirection=" + pagination.orderDirection
        : ""
    }${search.length > 0 ? "&search=" + search : ""}${
      statusFilter.length > 0 ? "&status=" + statusFilter : ""
    }${startDate.length > 0 ? "&startDate=" + startDate : ""}${
      endDate.length > 0 ? "&endDate=" + endDate : ""
    }`;
    const requestURL = `${TICKET_API_URL}sprint/list/${projectId}?${
      requestParams + optionalRequestParams
    }`;
    const options = {
      method: "GET",
    };

    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setLoading(false));

    if (err || !data) {
      handleApiError(err);
      return;
    }
    const results = data.data;
    const meta = data._metadata.pagination;

    yield put(actions.setList(results));
    yield put(actions.setPagination(meta));
  } catch (err: any) {
    yield put(actions.setLoading(false));
    handleApiError(err);
  }
}
export function* fetchCompletedSprintsRequest(action: { payload: string }) {
  try {
    yield delay(500);
    const id = action.payload;
    // yield put(actions.setLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/list/completed/${id}`;
    const options = {
      method: "GET",
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    const results = data.data;
    yield put(actions.setCompletedSprintList(results));
  } catch (err: any) {
    yield put(actions.setLoading(false));
    handleApiError(err);
  }
}
export function* updateCustomizeHeaderRequest(action: {
  payload: { id: string; callback: () => void };
}) {
  try {
    yield delay(500);
    const customizeCards = yield select(selectCustomizeTableHeader);
    const id = action.payload.id;
    const formData = {
      customizeCards: customizeCards,
    };
    yield put(actions.setButtonLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/update/customize-card/${id}`;
    const options = {
      method: "PUT",
      body: JSON.stringify(formData),
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setButtonLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    yield call(action.payload.callback);
  } catch (err: any) {
    yield put(actions.setButtonLoading(false));
    handleApiError(err);
  }
}
export function* createSprintRequest(action: {
  payload: { projectId: string; callback: () => void };
}) {
  try {
    yield delay(500);
    const form: ISprints = yield select(selectSprintFormDta);
    const projectId = action.payload.projectId;
    const formData = {
      name: form.name.name,
      description: form.description,
      projectId: projectId,
      scrumMasterId: form.scrumMasterId,
      sprintMembers: form.sprintMembers,
      startDate: form.startDate,
      endDate: form.endDate,
      sendRetrospectiveRequest: form.sendRetrospectiveRequest,
    };
    yield put(actions.setButtonLoading(true));
    const requestURL = `${TICKET_API_URL}sprint/create`;
    const options = {
      method: "POST",
      body: JSON.stringify(formData),
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setButtonLoading(false));
    if (err || !data) {
      const parsed = handleApiError(err, {
        showToast: false,
        returnParsed: true,
      });
      const parseMessage = parsedErrorMessage(err);
      switch (parsed?.type) {
        case ERROR_TYPE.NAME_EXIST:
          yield put(
            actions.updateFormErrors({
              key: "name.error",
              value: parseMessage?.message,
            })
          );
          break;
        default:
          handleApiError(err);
          break;
      }
      return;
    }
    toastService.success(data.message);
    yield call(action.payload.callback);
  } catch (err: any) {
    yield put(actions.setButtonLoading(false));
    handleApiError(err);
  }
}

export default function* SprintSaga() {
  yield takeLatest(actions.fetchSprintList, fetchSprintListRequest);
  yield takeLatest(actions.createSprint, createSprintRequest);

  yield takeLatest(actions.fetchCompletedSprints, fetchCompletedSprintsRequest);
  yield takeLatest(actions.fetchCustomizeCards, fetchCustomizeCardsRequest);
  yield takeLatest(actions.updateCustomizeHeader, updateCustomizeHeaderRequest);
}
